import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'

export function useRoles() {
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchRoles()
  }, [])

  const fetchRoles = async () => {
    try {
      setLoading(true)
      const { data, error: err } = await supabase
        .from('roles')
        .select('*')
        .order('name')
      if (err) throw err
      setRoles(data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createRole = async (roleData) => {
    const { data, error } = await supabase
      .from('roles')
      .insert([roleData])
      .select()
    if (error) throw error
    await fetchRoles()
    return data[0]
  }

  const updateRole = async (id, roleData) => {
    const { data, error } = await supabase
      .from('roles')
      .update(roleData)
      .eq('id', id)
      .select()
    if (error) throw error
    await fetchRoles()
    return data[0]
  }

  const deleteRole = async (id) => {
    const { error } = await supabase.from('roles').delete().eq('id', id)
    if (error) throw error
    await fetchRoles()
  }

  const fetchRoleDetails = async (roleId) => {
    const { data, error } = await supabase
      .from('roles')
      .select(`
        *,
        role_permissions(*),
        advisor_roles(*, advisors(id, full_name, email))
      `)
      .eq('id', roleId)
      .single()
    if (error) {
      // Fallback if nested tables don't exist yet
      const { data: basic, error: e2 } = await supabase
        .from('roles').select('*').eq('id', roleId).single()
      if (e2) throw e2
      return { ...basic, role_permissions: [], advisor_roles: [] }
    }
    return data
  }

  const upsertPermission = async (roleId, pageKey, canView) => {
    // Delete existing then insert to avoid constraint issues
    await supabase
      .from('role_permissions')
      .delete()
      .eq('role_id', roleId)
      .eq('page_key', pageKey)

    const { error } = await supabase
      .from('role_permissions')
      .insert({ role_id: roleId, page_key: pageKey, can_view: canView })
    if (error) throw error
  }

  const assignAdvisor = async (roleId, advisorId) => {
    const { error } = await supabase
      .from('advisor_roles')
      .insert([{ role_id: roleId, advisor_id: advisorId }])
    if (error) throw error
  }

  const unassignAdvisor = async (roleId, advisorId) => {
    const { error } = await supabase
      .from('advisor_roles')
      .delete()
      .eq('role_id', roleId)
      .eq('advisor_id', advisorId)
    if (error) throw error
  }

  return {
    roles,
    loading,
    error,
    createRole,
    updateRole,
    deleteRole,
    fetchRoleDetails,
    upsertPermission,
    assignAdvisor,
    unassignAdvisor,
    refetch: fetchRoles,
  }
}
